import { useLang } from 'hooks/lang';

import { BUG_REPORT_SEVERITIES, BugReportWithUser, bugReportByCode } from 'entities';

import { Card, Column, IconButton, Row, Tag } from 'components/Blocks';
import { EntityStatusTag } from 'components/Entities';
import { MultiLineText } from 'components/MultiLineText';
import { UserAvatar } from 'components/UserAvatar';
import { AttachedFiles } from 'components/Uploads';
import { LinkIcon } from 'components/Icons';
import { Comments } from 'components/Comments';

import { BugReportStatusChange } from './StatusChangeMenu';

import { getTimePassed } from 'utils/date';
import { RequestContextButton } from './RequestContext';

interface BugReportCardProps {
  report: BugReportWithUser;
  updateReport: (data: Partial<BugReportWithUser>) => void;
}

export function BugReportCard({ report, updateReport }: BugReportCardProps) {
  const { t } = useLang();

  const { status, timestamp, description, title, user, files, severity, link, messages } = report;

  const urgencyConfig = BUG_REPORT_SEVERITIES.find((s) => s.value === severity)!;

  return (
    <Card flow="column" gap="3">
      <Row gap="3">
        <EntityStatusTag
          target="bugReport"
          status={status}
          round={false}
          className="w-fit"
          size="medium"
        />

        <BugReportStatusChange report={report} updateReport={updateReport} />

        <span className="ml-auto text-xs">{getTimePassed(timestamp)}</span>
      </Row>

      <Row gap="2">
        <h5 className="text-lg">{title}</h5>

        {!!link && (
          <IconButton href={link} linkTarget={link} size="small">
            <LinkIcon size={14} />
          </IconButton>
        )}

        <Tag name={urgencyConfig.getLabel(t)} color={urgencyConfig.color} type="outlined" />
      </Row>

      <Column gap="1">
        <MultiLineText
          text={description}
          renderLine={(line, { isLast }) => (
            <p className="max-w-[95ch] text-justify leading-relaxed">
              {line}{' '}
              {isLast && <RequestContextButton report={report} updateReport={updateReport} />}
            </p>
          )}
        />
      </Column>

      <UserAvatar user={user} name size={32} />

      <AttachedFiles files={files} />

      <Comments
        canEdit={false}
        className="mt-2 border-0 p-0"
        addTitle={false}
        comments={messages.map((msg, index) => ({
          ...msg,
          id: `${index}`,
          user: { ...msg, id: msg.timestamp },
          text: msg.text || '-',
        }))}
        getExtraContent={(comment) =>
          !!comment.status && (
            <span className="ml-1 text-xs italic">
              {t('status-changed-to')}{' '}
              <span
                style={{ color: bugReportByCode[comment.status.to].color }}
                className="font-semibold"
              >
                {bugReportByCode[comment.status.to].getLabel(t)}
              </span>
            </span>
          )
        }
      />
    </Card>
  );
}
